import { useQuery } from "@tanstack/react-query";
import { api as appApi, type Api } from "../../data";
import type { TalentWorkspace } from "../../app/session";
import type { EInvoiceSummary } from "@talentkontor/domain";
import { mitServerPruefung, type EInvoicePruefung, type ServerPruefFelder } from "../../model/e-rechnung";

/**
 * Daten des Creator-Portals. Alle Lader nehmen den Anschluss als Parameter, damit die Tests dieselben
 * Abfragen gegen die Demo-Datenbank laufen lassen wie die Seiten. Jede Abfrage filtert nach der gewählten
 * Agentur und dem eigenen Talent-Datensatz; RLS begrenzt zusätzlich (Talents lesen nur Eigenes).
 * Agenturangaben kommen ausschließlich aus der Sicht org_profiles (S-10).
 */

export interface CreatorScope {
  talentId: string;
  orgId: string;
}

export function scopeOf(ws: TalentWorkspace): CreatorScope {
  return { talentId: ws.link.talent_id, orgId: ws.link.org_id };
}

// ── Zeilen (nur die Spalten, die das Creator-Portal nutzt) ──────────────────

export interface TalentSelf {
  id: string;
  org_id: string;
  talent_number: number | null;
  first_name: string;
  last_name: string | null;
  display_name: string | null;
  email: string | null;
  country: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  vat_id: string | null;
  tax_number: string | null;
  small_business: boolean;
  commission_bp: number | null;
  nickname: string | null;
  instagram_handle: string | null;
  tiktok_handle: string | null;
  youtube_handle: string | null;
  whatsapp_phone: string | null;
  whatsapp_opt_in_at: string | null;
  profile_image_path: string | null;
  status: string;
  ig_enabled: boolean;
  /** Gutschriftverfahren vereinbart (0046): die Agentur stellt Rechnungen im Namen des Creators aus */
  self_billing_agreed_on?: string | null;
  /** Eigene Firma (0042): Aussteller der Rechnung ist dann die Firma, vertreten durch die Person */
  company_name?: string | null;
  legal_form?: string | null;
  register_info?: string | null;
  /** Eigenes Logo für die im Portal erzeugte Rechnung, JPEG als data-URL (0080) */
  invoice_logo?: string | null;
  updated_at: string;
}

/** Stand der Auszahlung einer Rechnung an die Agentur (invoice_payout_info, 0055) */
export interface CPayoutInfo {
  invoice_id: string;
  mode: "fixed_terms" | "after_brand_payment";
  payout_due?: string | null;
  brand_state?: "paid" | "partial" | "open" | "not_invoiced" | "unlinked" | "written_off";
  brand_paid_on?: string | null;
}

export interface CDeal {
  id: string;
  org_id: string;
  deal_number: number | null;
  talent_id: string;
  brand_id: string;
  title: string;
  model: string;
  status: string;
  currency: string;
  budget_cents: number | null;
  commission_bp: number | null;
  /** Nachprovision nach Austritt (0074): Regel an der umgestellten Kooperation, Satz je Abrechnungsmonat */
  post_term?: unknown;
  post_term_from?: string | null;
  post_term_until?: string | null;
  start_month: string | null;
  months: number | null;
  deliverables: string | null;
  talent_note: string | null;
  platforms: string[] | null;
  usage_rights: string | null;
  usage_rights_until: string | null;
  link: string | null;
  content_deadline: string | null;
  products_flat_taxed: boolean;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  /** Wirksamer Abrechnungsweg (talent_deals, 0039) */
  invoice_route?: "via_agency" | "direct_to_brand" | null;
  /** Gestaffelte Provision und Agenturvergütung der Marke (talent_deals, 0075): der Anteil rechnet wie bei der Agentur */
  commission_tiers?: unknown;
  brand_discount_bp?: number | null;
  brand_discount_bearer?: string | null;
  /**
   * CREATOR-1 (0079): „share“ = die Agentur zeigt nur den Anteil; Budget, Monats- und Zusatzbeträge kommen dann schon als
   * Anteil des Creators (nach Provision), abzuziehen ist nichts mehr
   */
  amounts_basis?: "share" | "budget" | null;
}

export interface CPeriod {
  id: string;
  org_id: string;
  deal_id: string;
  month: string;
  amount_cents: number | null;
  status: string;
  manual_status: string | null;
  content_deadline: string | null;
  commission_forecast_cents: number | null;
  invoice_requested_at: string | null;
  request_note: string | null;
  suggested_items: unknown;
  booked_at: string | null;
  /** 0060/0095: Ausfallhonorar des Monats (bei „nur Anteil“ schon als Anteil des Creators) */
  cancellation_fee_cents?: number | null;
  /** 0064: im bisherigen System der Agentur schon abgerechnet bzw. bezahlt */
  imported_state?: "invoiced" | "paid" | null;
}

export interface CLineItem {
  id: string;
  deal_id: string;
  period_id: string | null;
  kind: string;
  description: string;
  amount_cents: number;
  commissionable: boolean;
}

export interface CBrand {
  id: string;
  name: string;
  legal_name: string | null;
  country: string | null;
}

/** Beitrag einer sichtbaren Kooperation (Sicht talent_deal_deliverables, 0068): ohne interne Notiz und Prüfvermerke */
export interface CDeliverable {
  id: string;
  deal_id: string;
  period_id: string | null;
  platform: string;
  format: string;
  format_label: string | null;
  title: string | null;
  draft_due: string | null;
  publish_on: string | null;
  status: "planned" | "draft_submitted" | "changes_requested" | "approved" | "live" | "cancelled";
  live_url: string | null;
  published_on: string | null;
  usage_rights_months: number | null;
  usage_rights_until: string | null;
  /** 0199 */
  usage_kinds?: string[] | null;
  usage_exclusive?: boolean | null;
  usage_territory?: string | null;
  sort_order: number;
  /** Entwurf vorab freigeben (0095); false = direkt live melden */
  approval_required?: boolean;
}
/** Einreichung zu einem Beitrag (deal_content_submissions, 0068): Stand und Feedback der Agentur */
export interface CDeliverableSubmission {
  id: string;
  deliverable_id: string;
  version: number;
  status: "submitted" | "changes_requested" | "approved";
  feedback: string | null;
  submitted_at: string;
  reviewed_at: string | null;
  /** Dateien der Content-Ablage (0155) */
  media_ids?: string[];
  /** Link bzw. Caption der Einreichung (Prüf-Chat CP-017: die Creatorin sieht, was sie eingereicht hat) */
  caption?: string | null;
}

/** Rechnungszeile mit allen Spalten, die invoiceFromRow und die Anzeige brauchen. */
export interface CInvoice {
  id: string;
  org_id: string;
  /**
   * Nur im Portal ergänzt (invoice_payout_info, 0055): Fälligkeit der Auszahlung nach dem Modus der Agentur;
   * null = erst nach Zahlung der Marke, fehlt = unbekannt (dann gilt die Frist der Rechnung)
   */
  payout_due?: string | null;
  /** Stand der Zahlung der Marke (invoice_payout_info), nur im Portal ergänzt (GELD-R3-3) */
  brand_state?: CPayoutInfo["brand_state"];
  /** Gebuchte Zahlungen, Verrechnungen und Einbehalte (0043); bei Creator-Rechnungen die anteilige Auszahlung (GELD-R3-3) */
  paid_cents?: number;
  /** Gutschrift vom Creator als geprüft bestätigt („Passt so“, 0091) */
  self_billing_checked_at?: string | null;
  kind: "talent_to_agency" | "agency_to_brand" | "talent_to_brand" | "agency_to_talent";
  deal_id: string | null;
  period_id: string | null;
  talent_id: string | null;
  brand_id: string | null;
  number: string | null;
  issue_date: string | null;
  service_period_start: string | null;
  service_period_end: string | null;
  revenue_month: string | null;
  currency: string;
  net_cents: number;
  tax_cents: number;
  /** 0267 (K-296): Umsatzsteuer in Euro bei fremder Rechnungswährung, beim Einreichen eingefroren */
  tax_eur_cents?: number | null;
  tax_eur_rate?: number | string | null;
  tax_eur_rate_on?: string | null;
  tax_eur_source?: string | null;
  gross_cents: number;
  tax_mode: string | null;
  notice_key: string | null;
  document_due_date: string | null;
  effective_due_date: string | null;
  issuer_due_date: string | null;
  status: "draft" | "submitted" | "revision_requested" | "rejected" | "approved" | "sent" | "paid" | "cancelled" | "withdrawn" | "discarded";
  dunning_stage: string;
  dunning_blocked: boolean;
  in_collection: boolean;
  pdf_path: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  revision_reason: string | null;
  /** Beschreibung des Creators; Pflicht bei Rechnungen ohne Kooperation (0080) */
  talent_note?: string | null;
  rejection_mail_sent_at: string | null;
  approved_at: string | null;
  sent_at: string | null;
  sent_to_brand_count: number;
  last_sent_to_brand_at: string | null;
  last_dunning_at: string | null;
  dunning_deadline: string | null;
  collection_at: string | null;
  cancelled_at: string | null;
  cancel_reason?: string | null;
  paid_at: string | null;
  payment_reference: string | null;
  talent_payment_response: "paid" | "still_overdue" | null;
  talent_payment_response_at: string | null;
  overdue_check_requested_at: string | null;
  admin_payment_confirmed_at: string | null;
  discarded_at: string | null;
  withdrawn_at: string | null;
  /** Herkunft des Rechnungs-PDFs (0045): erzeugt aus den Angaben oder selbst hochgeladen */
  pdf_origin?: "generated" | "uploaded" | null;
  /** Bestätigung des Creators, dass Nummer und Betrag der eigenen Datei stimmen (0045) */
  pdf_confirmed_at?: string | null;
  /** Gutschrift: die Agentur hat die Rechnung im Namen des Creators ausgestellt (0046, § 14 Abs. 2 UStG) */
  self_billed?: boolean;
  /** Stornobeleg zu dieser Rechnung bzw. Gutschrift (0043, 0345): hebt sie auf, ist selbst nicht widersprechbar */
  credit_for?: string | null;
  /** Widerspruch des Creators gegen die Gutschrift (0046); danach ist sie storniert */
  objected_at?: string | null;
  objection_reason?: string | null;
  /** Eingefrorener Empfänger (0036); im Archiv die einzige Angabe zur Gegenseite */
  recipient_snapshot?: { name?: string } | null;
  created_at: string;
  updated_at: string;
}

/** Eigene Rechnungsnummer in irgendeiner Agentur (my_invoice_numbers, 0045): ohne Agenturdaten */
export interface OwnNumber {
  invoice_id: string;
  talent_id: string;
  number: string;
  issue_date: string | null;
  status: string;
  /** Gutschrift der Agentur (0458, Prüf-Chat CP-021): zählt nicht für den Nummernvorschlag; fehlt bei älterem Stand */
  self_billed?: boolean | null;
}

/** Wirksamer Provisionssatz je Kooperation (talent_deal_rates, 0031): Kooperation → Profil → Agentur-Standard */
export interface DealRate {
  deal_id: string;
  commission_bp: number | null;
  source: "deal" | "talent" | "agency";
}

export interface CBooking {
  id: string;
  deal_id: string | null;
  period_id: string | null;
  brand_id: string | null;
  kind: string;
  booked_on: string;
  /** Bei „Nur ihren Anteil“ der Anteil des Creators (0079), sonst der gebuchte Betrag */
  amount_cents: number | null;
  currency: string;
  talent_share_cents: number | null;
  status: string;
}

/** Was ein Talent über seine Agentur wissen darf (Sicht org_profiles). */
export interface OrgAddress {
  org_id: string;
  name: string;
  legal_name: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  vat_id: string | null;
  locale: string;
  currency: string;
  timezone: string;
  default_document_language: string | null;
  /** Konditionen und Prüfregeln der Agentur für Creator (0031); ohne Angabe gilt der Plattform-Standard */
  talent_payment_terms_days?: number | null;
  invoice_route?: string | null;
  invoice_number_scheme?: unknown;
  expense_keywords?: string[] | null;
  min_line_description_chars?: number | null;
  invoice_date_workdays_only?: boolean | null;
  budget_deviation_percent?: number | string | null;
  due_soon_days?: number | null;
  holiday_region?: string | null;
  talent_profile_requirements?: string[] | null;
  currencies?: string[] | null;
  /** Auszahlung an Creator: feste Frist oder nach Zahlungseingang der Marke (0042, lesbar seit 0052) */
  talent_payout_mode?: string | null;
}

/** Rechnungsempfänger bei direkter Abrechnung (Sicht talent_deal_billing, 0039) */
export interface BillingParty {
  name?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  vat_id?: string;
  register?: string;
  email?: string;
  reference?: string;
  on_behalf_of?: string;
  /** K-334: false = Marke als Privatperson erfasst */
  business?: boolean;
}

export type PlatformDefaults = Record<string, unknown>;

export interface CreatorBase {
  talent: TalentSelf;
  deals: CDeal[];
  periods: CPeriod[];
  lineItems: CLineItem[];
  brands: Map<string, CBrand>;
  /** Eigene Rechnungen des Creators (an die Agentur oder direkt an die Marke) */
  invoices: CInvoice[];
  /** Rechnungen der Agentur an den Creator (Provision, Gebühr; 0039), sichtbar ab dem Versand */
  agencyInvoices: CInvoice[];
  bookings: CBooking[];
  org: OrgAddress;
  defaults: PlatformDefaults;
  /** Kooperationen mit direkter Abrechnung: Rechnungsempfänger (Marke oder deren Agentur) */
  billing: Map<string, BillingParty>;
  /** Wirksamer Provisionssatz je Kooperation, auch wenn er aus dem Agentur-Standard kommt (Befund 2) */
  rates: Map<string, DealRate>;
  /** Eigene Rechnungsnummern über alle Agenturen (Befund 10) */
  ownNumbers: OwnNumber[];
  /** Plattform-Katalog der Agentur (0047, lesbar seit 0053): Schlüssel → Bezeichnung */
  platformNames: Map<string, { de: string; en: string | null }>;
  /** Auszahlung je Rechnung an die Agentur: Fälligkeit nach Auszahlungsmodus und Zahlung der Marke (0055) */
  payoutInfo: Map<string, CPayoutInfo>;
  /** Beiträge der sichtbaren Kooperationen und ihre Einreichungen (0068) */
  deliverables: CDeliverable[];
  deliverableSubmissions: CDeliverableSubmission[];
  /** Eigene Statusbezeichnungen der Agentur (org_status_labels, für Creator lesbar; SAAS-10) */
  statusLabels?: Map<string, { de: string | null; en: string | null }>;
}

// ── Lader ───────────────────────────────────────────────────────────────────

const byOrg = (s: CreatorScope) => [["org_id", "eq", s.orgId]] as const;

/** Stammdaten, Kooperationen, Monate, Rechnungen und Buchungen des Creators in der gewählten Agentur. */
export async function loadCreatorBase(api: Api, s: CreatorScope): Promise<CreatorBase> {
  const [talents, deals, invoices, bookings, orgs, defaults] = await Promise.all([
    api.select<TalentSelf>("talents", { filters: [["id", "eq", s.talentId], ["org_id", "eq", s.orgId]] }),
    // Creator lesen Sichten ohne interne Spalten der Agentur (0037), nie die Rohtabellen
    api.select<CDeal>("talent_deals", { filters: [...byOrg(s), ["talent_id", "eq", s.talentId]], order: [["created_at", "desc"]] }),
    api.select<CInvoice>("invoices", { filters: [...byOrg(s), ["talent_id", "eq", s.talentId]], order: [["created_at", "desc"]] }),
    // Buchungen, die die Agentur für den Creator sichtbar gemacht hat (Bonus, Nutzungsrechte …)
    api.select<CBooking>("talent_bookings", {
      columns: "id, deal_id, period_id, brand_id, kind, booked_on, amount_cents, currency, talent_share_cents, status",
      filters: [...byOrg(s), ["talent_id", "eq", s.talentId]],
    }),
    api.select<OrgAddress>("org_profiles", { filters: byOrg(s) }),
    api.select<{ key: string; value: unknown }>("talent_platform_defaults", { columns: "key, value" }),
  ]);
  const talent = talents[0];
  if (!talent) throw new Error("Dein Creator-Profil in dieser Agentur wurde nicht gefunden.");
  const org = orgs[0];
  if (!org) throw new Error("Die Angaben deiner Agentur sind nicht lesbar.");
  const dealIds = deals.map((d) => d.id);
  const brandIds = [...new Set(deals.map((d) => d.brand_id))];
  const [periods, lineItems, brands, billing, rates, ownNumbers, platforms, deliverables, deliverableSubmissions, statusLabels] = await Promise.all([
    dealIds.length
      ? api.select<CPeriod>("talent_deal_periods", { filters: [...byOrg(s), ["deal_id", "in", dealIds]], order: [["month", "asc"]] })
      : Promise.resolve([] as CPeriod[]),
    dealIds.length
      ? api.select<CLineItem>("talent_deal_line_items", {
          columns: "id, deal_id, period_id, kind, description, amount_cents, commissionable",
          filters: [...byOrg(s), ["deal_id", "in", dealIds]],
        })
      : Promise.resolve([] as CLineItem[]),
    brandIds.length
      ? api.select<CBrand>("talent_brands", { columns: "id, name, legal_name, country", filters: [...byOrg(s), ["id", "in", brandIds]] })
      : Promise.resolve([] as CBrand[]),
    dealIds.length
      ? api.select<{ deal_id: string; party: BillingParty | null }>("talent_deal_billing", { columns: "deal_id, party", filters: [...byOrg(s), ["deal_id", "in", dealIds]] }).catch(() => [])
      : Promise.resolve([] as { deal_id: string; party: BillingParty | null }[]),
    dealIds.length ? api.read<DealRate[]>("talent_deal_rates", {}).catch(() => [] as DealRate[]) : Promise.resolve([] as DealRate[]),
    api.read<OwnNumber[]>("my_invoice_numbers", {}).catch(() => [] as OwnNumber[]),
    api.select<{ key: string; label_de: string; label_en: string | null }>("org_deal_platforms", { columns: "key, label_de, label_en", filters: byOrg(s) })
      .catch(() => [] as { key: string; label_de: string; label_en: string | null }[]),
    // 0068: Beiträge (Sicht ohne interne Angaben) und Einreichungen zu Beiträgen (Feedback der Agentur)
    dealIds.length
      ? api.select<CDeliverable>("talent_deal_deliverables", {
          columns: "id, deal_id, period_id, platform, format, format_label, title, draft_due, publish_on, status, live_url, published_on, usage_rights_months, usage_rights_until, usage_kinds, usage_exclusive, usage_territory, sort_order",
          filters: [...byOrg(s), ["deal_id", "in", dealIds]], order: [["sort_order", "asc"], ["created_at", "asc"]],
        }).catch(() => [] as CDeliverable[])
      : Promise.resolve([] as CDeliverable[]),
    dealIds.length
      ? api.select<CDeliverableSubmission>("deal_content_submissions", {
          columns: "id, deliverable_id, version, status, feedback, submitted_at, reviewed_at, media_ids, caption",
          filters: [...byOrg(s), ["deal_id", "in", dealIds], ["deliverable_id", "not_is", null]], order: [["submitted_at", "desc"]],
        }).catch(() => [] as CDeliverableSubmission[])
      : Promise.resolve([] as CDeliverableSubmission[]),
    api.select<{ status: string; label_de: string | null; label_en: string | null }>("org_status_labels", { columns: "status, label_de, label_en", filters: byOrg(s) })
      .catch(() => [] as { status: string; label_de: string | null; label_en: string | null }[]),
  ]);
  const mine = new Set(dealIds);
  const payoutIds = invoices.filter((i) => i.kind === "talent_to_agency" && ["approved", "sent"].includes(i.status)).map((i) => i.id);
  const payoutInfo = payoutIds.length ? await api.read<CPayoutInfo[]>("invoice_payout_info", { p_invoices: payoutIds }).catch(() => [] as CPayoutInfo[]) : [];
  return {
    talent,
    deals,
    periods,
    lineItems,
    brands: new Map(brands.map((b) => [b.id, b])),
    // Fälligkeit der Auszahlung an der Rechnung: Liste, Übersicht und Kooperation zeigen denselben Stand (Befund Creator 6)
    invoices: invoices.filter((i) => i.kind === "talent_to_agency" || i.kind === "talent_to_brand")
      .map((i) => { const p = (payoutInfo ?? []).find((x) => x.invoice_id === i.id); return p ? { ...i, payout_due: p.payout_due ?? null, ...(p.brand_state ? { brand_state: p.brand_state } : {}) } : i; }),
    agencyInvoices: invoices.filter((i) => i.kind === "agency_to_talent"),
    bookings,
    org,
    defaults: Object.fromEntries(defaults.map((d) => [d.key, d.value])),
    billing: new Map(billing.filter((x) => x.party).map((x) => [x.deal_id, x.party!])),
    rates: new Map((rates ?? []).filter((r) => mine.has(r.deal_id)).map((r) => [r.deal_id, r])),
    ownNumbers: ownNumbers ?? [],
    platformNames: new Map(platforms.map((x) => [x.key, { de: x.label_de, en: x.label_en }])),
    payoutInfo: new Map((payoutInfo ?? []).map((p) => [p.invoice_id, p])),
    deliverables,
    deliverableSubmissions,
    statusLabels: new Map(statusLabels.map((l) => [l.status, { de: l.label_de, en: l.label_en }])),
  };
}

/** Eigene Dateien aus dem Chat (Auswahl beim Einreichen eines Entwurfs, 0068): nur, was der Creator selbst geschickt hat. */
export async function loadOwnChatFiles(api: Api, s: CreatorScope): Promise<CAttachment[]> {
  const messages = await api.select<{ id: string }>("messages", {
    columns: "id", filters: [...byOrg(s), ["talent_id", "eq", s.talentId], ["sender_side", "eq", "talent"]], order: [["created_at", "desc"]], limit: 60,
  });
  if (!messages.length) return [];
  return api.select<CAttachment>("attachments", {
    columns: "id, owner_id, storage_path, file_name, mime_type, size_bytes, created_at",
    filters: [["owner_kind", "eq", "chat"], ["owner_id", "in", messages.map((m) => m.id)], ["deleted_at", "is", null]],
    order: [["created_at", "desc"]],
  });
}

/**
 * Belege bei einer früheren Agentur (archiviertes Profil, 0045): eigene Rechnungen und Rechnungen der Agentur an den
 * Creator. Kooperationen, Marken und Profil sind dort nicht mehr lesbar; die Gegenseite nennt der eingefrorene Empfänger.
 */
export async function loadArchivedInvoices(api: Api, s: CreatorScope): Promise<{ invoices: CInvoice[]; agencyInvoices: CInvoice[]; defaults: PlatformDefaults; talent?: TalentSelf }> {
  const [rows, defaults, me] = await Promise.all([
    api.select<CInvoice>("invoices", { filters: [...byOrg(s), ["talent_id", "eq", s.talentId]], order: [["created_at", "desc"]] }),
    api.select<{ key: string; value: unknown }>("talent_platform_defaults", { columns: "key, value" }),
    // für Gutschriften/Agenturrechnungen ohne archiviertes PDF (Neuerzeugung aus den eingefrorenen Angaben)
    api.select<TalentSelf>("talents", { filters: [["id", "eq", s.talentId], ["org_id", "eq", s.orgId]] }).catch(() => [] as TalentSelf[]),
  ]);
  return {
    invoices: rows.filter((i) => i.kind === "talent_to_agency" || i.kind === "talent_to_brand"),
    agencyInvoices: rows.filter((i) => i.kind === "agency_to_talent"),
    defaults: Object.fromEntries(defaults.map((d) => [d.key, d.value])),
    ...(me[0] ? { talent: me[0] } : {}),
  };
}

export interface CInvoiceLine {
  id: string;
  invoice_id: string;
  position: number;
  description: string;
  quantity: number;
  unit_cents: number;
  tax_rate_bp: number;
  net_cents: number;
  commission_free: boolean;
}
export interface CAttachment extends ServerPruefFelder {
  id: string;
  owner_id: string;
  storage_path: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
  /** invoice, receipt … (0031) */
  category?: string | null;
  /** Steuerprüfung L12 (0446): Prüfsumme der Datei und Kurzfassung der E-Rechnung */
  sha256?: string | null;
  e_invoice?: EInvoiceSummary | null;
  /** E-1 (0448): nach mitServerPruefung die Angabe des Browsers und die Herkunft der Werte */
  e_invoice_browser?: EInvoiceSummary | null;
  pruefung?: EInvoicePruefung | null;
}
export interface CInvoiceEvent {
  id: number;
  invoice_id: string;
  event: string;
  at: string;
  details: Record<string, unknown>;
}
export interface InvoiceDetails {
  lines: CInvoiceLine[];
  attachments: CAttachment[];
  events: CInvoiceEvent[];
}

/** Positionen, Belege und Protokoll zu eigenen Rechnungen. */
export async function loadInvoiceDetails(api: Api, s: CreatorScope, invoiceIds: readonly string[]): Promise<InvoiceDetails> {
  if (!invoiceIds.length) return { lines: [], attachments: [], events: [] };
  const ids = [...invoiceIds];
  const [lines, attachments, events] = await Promise.all([
    api.select<CInvoiceLine>("invoice_lines", { filters: [...byOrg(s), ["invoice_id", "in", ids]], order: [["position", "asc"]] }),
    // E-1 (0448): sha256 und e_invoice sind nach der serverseitigen Prüfung die Werte des Servers
    api.select<CAttachment>("attachments", {
      columns: "id, owner_id, storage_path, file_name, mime_type, size_bytes, created_at, category, sha256, e_invoice, sha256_server, e_invoice_server, e_invoice_check, e_invoice_checked_at, e_invoice_check_error, e_invoice_mismatch",
      filters: [...byOrg(s), ["owner_kind", "eq", "invoice"], ["owner_id", "in", ids]],
      order: [["created_at", "asc"]],
    }).then((r) => r.map(mitServerPruefung)),
    api.select<CInvoiceEvent>("talent_invoice_events", {
      columns: "id, invoice_id, event, at, details",
      filters: [...byOrg(s), ["invoice_id", "in", ids]],
      order: [["at", "asc"]],
    }),
  ]);
  return { lines, attachments, events };
}

export interface CContract {
  id: string;
  deal_id: string | null;
  title: string;
  status: string;
  sent_at: string | null;
  completed_at: string | null;
  created_at: string;
  /** Für den Stand wie in „Deine Verträge“ (Befund 10): abgelehnt und angehalten, Kooperation storniert, wer unterschreibt */
  declined_at?: string | null;
  deal_cancelled?: boolean;
  talent_signs?: boolean;
  guardian_signs?: boolean;
  /** 0093 (CREATOR-R3-2): minderjährig ohne gesetzliche Vertretung, Unterschreiben gesperrt */
  guardian_required?: boolean;
}
/** Stand eines Unterzeichners, wie ihn das Talent sieht (ohne Kontaktdaten, 0032). */
export interface CSigner {
  contract_id: string;
  role: string;
  /** party = Vertragspartei selbst, legal_guardian = gesetzliche Vertretung (0040) */
  capacity?: "party" | "legal_guardian";
  name: string;
  signed: boolean;
  signed_at: string | null;
  signing_name: string | null;
  declined: boolean;
  /** Kompatibilität: ungültig gewordene Unterschriften kommen als signed = false */
  invalidated_at: string | null;
}

/** Verträge ab Versand über die Sicht talent_contracts (ohne verborgene Anlage, ohne Kontaktdaten); hier nur Kopfdaten. */
export async function loadContracts(api: Api, s: CreatorScope): Promise<{ contracts: CContract[]; signers: CSigner[] }> {
  const rows = await api.select<CContract & { signers: Omit<CSigner, "contract_id" | "invalidated_at">[] }>("talent_contracts", {
    columns: "id, deal_id, title, status, sent_at, completed_at, created_at, declined_at, deal_cancelled, talent_signs, guardian_signs, guardian_required, signers",
    filters: [...byOrg(s), ["talent_id", "eq", s.talentId]],
    order: [["created_at", "desc"]],
  });
  const contracts = rows.map(({ signers: _s, ...c }) => c);
  const signers = rows.flatMap((r) => (r.signers ?? []).map((x) => ({ ...x, contract_id: r.id, invalidated_at: null })));
  return { contracts, signers };
}

export interface CPayout {
  talent_id: string;
  org_id: string;
  iban: string | null;
  bic: string | null;
  bank_account_holder: string | null;
  bank_name: string | null;
  bank_country: string | null;
  /** Auszahlungsweg (0042): sepa, swift, paypal, wise; fehlt die Spalte, gilt SEPA mit IBAN */
  payout_method?: "sepa" | "swift" | "paypal" | "wise" | null;
  account_number?: string | null;
  routing_code?: string | null;
  payout_email?: string | null;
  payout_currency?: string | null;
}
export interface CSignature {
  talent_id: string;
  org_id: string;
  signing_name: string;
  image_path: string;
  updated_at: string;
}
export interface CPreferences {
  user_id: string;
  language: string | null;
  initials: string | null;
  desktop_notifications: boolean;
  onboarding: Record<string, unknown>;
}
export interface ProfileExtras {
  payout: CPayout | null;
  signature: CSignature | null;
  prefs: CPreferences | null;
}

export async function loadProfileExtras(api: Api, s: CreatorScope, userId: string): Promise<ProfileExtras> {
  const [payout, signature, prefs] = await Promise.all([
    api.select<CPayout>("talent_payout_details", { filters: [["talent_id", "eq", s.talentId], ...byOrg(s)] }),
    api.select<CSignature>("talent_signatures", { filters: [["talent_id", "eq", s.talentId], ...byOrg(s)] }),
    api.select<CPreferences>("user_preferences", { filters: [["user_id", "eq", userId]] }),
  ]);
  return { payout: payout[0] ?? null, signature: signature[0] ?? null, prefs: prefs[0] ?? null };
}

export interface CArticle {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  default_price_cents: number | null;
  currency: string | null;
  unit: string | null;
  commissionable: boolean;
  sort_order: number;
}

/** Artikelkatalog der Agentur (nur aktive Artikel sind für Talents lesbar). */
export async function loadArticles(api: Api, s: CreatorScope): Promise<CArticle[]> {
  return api.select<CArticle>("invoice_articles", {
    columns: "id, name, description, category, default_price_cents, currency, unit, commissionable, sort_order",
    filters: [...byOrg(s), ["active", "is", true]],
    order: [["sort_order", "asc"], ["name", "asc"]],
  });
}

export interface IgAccount {
  talent_id: string;
  username: string;
  name: string | null;
  followers_count: number | null;
  media_count: number | null;
  status: string;
  connected_at: string;
  last_sync_at: string | null;
  last_error: string | null;
  /** 0156: Stand des Abgleichs (Knopf „Aktualisieren“, Fehler als Liste {code, step, message}) */
  sync_started_at?: string | null;
  sync_finished_at?: string | null;
  sync_errors?: IgSyncError[] | null;
  /** 0164: Profilbild im Bucket ig-media */
  profile_picture_path?: string | null;
}
export interface IgSyncError {
  code: string;
  step: string;
  message: string;
}
export interface IgMonthly {
  month: string;
  metrics: Record<string, number | null>;
}
export interface IgConsent {
  talent_id: string;
  org_id: string;
  sharing_allowed: boolean;
  allowed_blocks: string[];
  updated_at: string;
}
export interface IgShare {
  id: string;
  brand_id: string | null;
  recipient_email: string;
  blocks: string[];
  period: string;
  expires_at: string;
  revoked_at: string | null;
  locked_at: string | null;
  sent_at: string | null;
  created_at: string;
}
export interface IgPeriodTotals {
  period: string;
  computed_on: string;
  metrics: Record<string, number | null>;
}
export interface IgMedia {
  media_id: string;
  media_type: string | null;
  permalink: string | null;
  caption: string | null;
  posted_at: string | null;
  metrics: Record<string, number | null>;
  /** 0164: Vorschaubild im Bucket ig-media */
  thumbnail_path?: string | null;
}
/** Tageswerte (0023, 90 Tage) für die Verlaufskurve der eigenen Insights */
export interface IgDaily {
  day: string;
  metrics: Record<string, number | null>;
}
export interface IgAudience {
  breakdown: string;
  captured_on: string;
  data: Record<string, number>;
  /** 0340: Zeitfenster, das Instagram geliefert hat (last_90_days …) */
  timeframe?: string | null;
}
/** 0340: Werte je Story (nur 24 Stunden bei Instagram abrufbar, bei jedem Abgleich gesichert) */
export interface IgStory {
  story_id: string;
  media_type: string | null;
  posted_at: string;
  metrics: Record<string, unknown>;
  few_viewers: boolean;
  measured_at: string | null;
  measurements: number;
}
export interface InstagramData {
  account: IgAccount | null;
  consent: IgConsent | null;
  shares: IgShare[];
  totals: IgPeriodTotals[];
  media: IgMedia[];
  audience: IgAudience[];
  /** Monatswerte (bleiben dauerhaft, 0023) für 12 Monate und den Gesamtverlauf */
  monthly: IgMonthly[];
  /** Tageswerte für die Verlaufskurve (0164) */
  daily?: IgDaily[];
  /** Storys einzeln (0340) */
  stories?: IgStory[];
}

export async function loadInstagram(api: Api, s: CreatorScope): Promise<InstagramData> {
  const mine = [["talent_id", "eq", s.talentId], ...byOrg(s)] as const;
  const [accounts, consent, shares, totals, media, audience, monthly, daily, stories] = await Promise.all([
    api.select<IgAccount>("ig_accounts", {
      columns: "talent_id, username, name, followers_count, media_count, status, connected_at, last_sync_at, last_error, sync_started_at, sync_finished_at, sync_errors, profile_picture_path",
      filters: mine,
    }),
    api.select<IgConsent>("ig_share_consent", { filters: mine }),
    api.select<IgShare>("ig_shares", {
      columns: "id, brand_id, recipient_email, blocks, period, expires_at, revoked_at, locked_at, sent_at, created_at",
      filters: mine,
      order: [["created_at", "desc"]],
    }),
    api.select<IgPeriodTotals>("ig_period_totals", { columns: "period, computed_on, metrics", filters: mine, order: [["computed_on", "desc"]] }),
    api.select<IgMedia>("ig_media", { columns: "media_id, media_type, permalink, caption, posted_at, metrics, thumbnail_path", filters: mine, order: [["posted_at", "desc"]], limit: 60 }),
    api.select<IgAudience>("ig_audience", { columns: "breakdown, captured_on, data, timeframe", filters: mine, order: [["captured_on", "desc"]] }),
    api.select<IgMonthly>("ig_monthly", { columns: "month, metrics", filters: mine, order: [["month", "asc"]] }),
    api.select<IgDaily>("ig_daily", { columns: "day, metrics", filters: mine, order: [["day", "desc"]], limit: 92 }),
    api.select<IgStory>("ig_stories", { columns: "story_id, media_type, posted_at, metrics, few_viewers, measured_at, measurements", filters: mine, order: [["posted_at", "desc"]], limit: 120 }),
  ]);
  return { account: accounts[0] ?? null, consent: consent[0] ?? null, shares, totals, media, audience, monthly, daily, stories };
}

export interface FxRate {
  base: string;
  quote: string;
  valid_on: string;
  rate: number;
}

/** Wechselkurse der Plattform (für jeden Angemeldeten lesbar, 0019). */
export async function loadFxRates(api: Api): Promise<FxRate[]> {
  // Ein Kurs je Monat (0076), sonst schneidet die Zeilengrenze der Schnittstelle die neuesten Tageskurse ab
  return api.select<FxRate>("fx_rates_monthly", { columns: "base, quote, valid_on, rate", order: [["valid_on", "asc"]] });
}

// ── Hooks (Schlüssel beginnen mit dem Talent: ein Agenturwechsel lädt alles neu) ──

// Praxistest G-138 (Last): alle 5 Minuten und nur bei sichtbarem Tab; beim Zurückkehren ins Fenster sofort
const live = { refetchOnWindowFocus: true, refetchInterval: 300_000, refetchIntervalInBackground: false } as const;

export function useArchivedInvoices(s: CreatorScope, enabled = true) {
  return useQuery({ queryKey: ["creator", s.talentId, "archived"], enabled, queryFn: () => loadArchivedInvoices(appApi, s), ...live });
}
export function useCreatorBase(s: CreatorScope, enabled = true) {
  return useQuery({ queryKey: ["creator", s.talentId, "base"], enabled, queryFn: () => loadCreatorBase(appApi, s), ...live });
}
export function useInvoiceDetails(s: CreatorScope, invoiceIds: readonly string[]) {
  const ids = [...invoiceIds].sort();
  return useQuery({ queryKey: ["creator", s.talentId, "invoice-details", ids.join(",")], queryFn: () => loadInvoiceDetails(appApi, s, ids), ...live });
}
export function useContracts(s: CreatorScope) {
  return useQuery({ queryKey: ["creator", s.talentId, "contracts"], queryFn: () => loadContracts(appApi, s), ...live });
}
export function useProfileExtras(s: CreatorScope, userId: string | null) {
  return useQuery({
    queryKey: ["creator", s.talentId, "profile", userId],
    enabled: !!userId,
    queryFn: () => loadProfileExtras(appApi, s, userId as string),
  });
}
export function useArticles(s: CreatorScope) {
  return useQuery({ queryKey: ["creator", s.talentId, "articles"], queryFn: () => loadArticles(appApi, s), staleTime: 5 * 60_000 });
}
export function useFxRates() {
  return useQuery({ queryKey: ["creator-fx"], queryFn: () => loadFxRates(appApi), staleTime: 60 * 60_000 });
}
export function useInstagram(s: CreatorScope) {
  return useQuery({ queryKey: ["creator", s.talentId, "instagram"], queryFn: () => loadInstagram(appApi, s) });
}
/** Nur der Stand der Instagram-Verbindung (Profil, SICHT-CREATOR-22): active, expired, revoked oder null ohne Verbindung. */
export function useInstagramStatus(s: CreatorScope, enabled: boolean) {
  return useQuery({
    queryKey: ["creator", s.talentId, "instagram", "status"],
    enabled,
    queryFn: async () => (await appApi.select<{ status: string }>("ig_accounts", { columns: "status", filters: [["talent_id", "eq", s.talentId], ...byOrg(s)] }))[0]?.status ?? null,
  });
}
